import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Visibility, CheckCircle, Cancel } from '@mui/icons-material';
import accountingService from '../../services/accounting.service';
import { useLanguage } from '../../i18n/LanguageContext';

const JournalEntryList: React.FC = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadEntries(); }, []);

  const loadEntries = async () => {
    try {
      const res = await accountingService.getJournalEntries();
      setEntries(res.data);
    } catch (error) { console.error('Error loading entries:', error); }
    finally { setLoading(false); }
  };

  const handlePost = async (id: number) => {
    if (!window.confirm('Post this journal entry? This will update account balances.')) return;
    try {
      await accountingService.postJournalEntry(id);
      loadEntries();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error posting entry');
    }
  };

  const handleVoid = async (id: number) => {
    if (!window.confirm('Void this journal entry? This will reverse account balances if posted.')) return;
    try {
      await accountingService.voidJournalEntry(id);
      loadEntries();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error voiding entry');
    }
  };

  const getStatusColor = (status: string): "default" | "warning" | "success" | "error" => {
    const colors: Record<string, any> = { 'DRAFT': 'warning', 'POSTED': 'success', 'VOID': 'error' };
    return colors[status] || 'default';
  };

  const columns: GridColDef[] = [
    { field: 'entryNumber', headerName: t('journal.entry_number'), width: 200 },
    {
      field: 'entryDate', headerName: t('journal.entry_date'), width: 120,
      renderCell: (params: GridRenderCellParams) => new Date(params.value).toLocaleDateString()
    },
    { field: 'reference', headerName: t('journal.reference'), width: 150 },
    { field: 'description', headerName: t('common.description'), flex: 1 },
    {
      field: 'totalDebit', headerName: t('journal.debit'), width: 120, align: 'right', headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => `$${Number(params.value).toFixed(2)}`
    },
    {
      field: 'totalCredit', headerName: t('journal.credit'), width: 120, align: 'right', headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => `$${Number(params.value).toFixed(2)}`
    },
    { field: 'lineCount', headerName: t('journal.lines'), width: 80, align: 'center' },
    {
      field: 'status', headerName: t('common.status'), width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} color={getStatusColor(params.value as string)} size="small" />
      )
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 150, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.view')}>
            <IconButton size="small" onClick={() => navigate(`/journal-entries/${params.row.id}`)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'DRAFT' && (
            <Tooltip title={t('journal.post')}>
              <IconButton size="small" color="success" onClick={() => handlePost(params.row.id)}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status !== 'VOID' && (
            <Tooltip title={t('journal.void')}>
              <IconButton size="small" color="error" onClick={() => handleVoid(params.row.id)}>
                <Cancel fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('journal.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/journal-entries/new')}>
          {t('journal.new')}
        </Button>
      </Box>
      <DataGrid rows={entries} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50]} disableRowSelectionOnClick />
    </Box>
  );
};

export default JournalEntryList;
